import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SocialUniverseItem } from "@digital-twins/shared-types";
import { intlLocaleForUi } from "@/i18n";
import { cn } from "@/lib/utils";
import { socialUniverseCardState } from "./SocialUniverseCard";
import { isChannelInProgress, socialUniverseUpdatedAt } from "./socialTrackerStatus";
import { useTweenedNumber } from "./useTweenedNumber";

/** What the Topic's status pill says: live while channels collect or posts are sorted, the last update otherwise. */
export type SocialTopicStatus =
  | "collecting"
  | "queued"
  | "sorting"
  | "updated"
  | "not-updated";

export function socialTopicStatus(
  universe: Pick<
    SocialUniverseItem,
    "channels" | "postsPending" | "totalPosts" | "lastSyncedAt"
  >,
): SocialTopicStatus {
  const state = socialUniverseCardState(universe);
  const inProgress = universe.channels.filter((channel) =>
    isChannelInProgress(channel),
  );
  // `every` on an empty list is true: no channel in progress is never "queued".
  if (state === "collecting" && inProgress.length > 0) {
    return inProgress.every((channel) => channel.status === "queued")
      ? "queued"
      : "collecting";
  }
  if (state === "sorting") {
    return "sorting";
  }
  return socialUniverseUpdatedAt(universe) ? "updated" : "not-updated";
}

function formatUpdatedDay(iso: string, locale: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(date);
  } catch {
    return null;
  }
}

interface SocialTopicStatusPillProps {
  universe: SocialUniverseItem;
  /** Opens the Topic's Activity; absent renders a plain pill. */
  onOpenActivity?: () => void;
}

/**
 * The Topic's live status as plain muted text among the header actions: a
 * breathing dot only while channels collect or posts are sorted, "Queued"
 * while a channel waits, "Updated <date>" when idle (no dot). Only what moves: the post count
 * shows here while collecting, nowhere else in the header.
 */
export function SocialTopicStatusPill(props: SocialTopicStatusPillProps) {
  const { universe, onOpenActivity } = props;

  const { t, i18n } = useTranslation("app");
  const locale = intlLocaleForUi(i18n.language);

  // The counts tween between polls so the pill moves instead of jumping.
  const shownPosts = useTweenedNumber(universe.totalPosts) ?? universe.totalPosts;
  const shownPending =
    useTweenedNumber(universe.postsPending) ?? universe.postsPending;
  const numberFormat = useMemo(() => new Intl.NumberFormat(locale), [locale]);

  const status = socialTopicStatus(universe);
  const updatedAt = socialUniverseUpdatedAt(universe);
  const updated = updatedAt ? formatUpdatedDay(updatedAt, locale) : null;

  const label =
    status === "collecting"
      ? t("socialListening.topicPage.status.collecting", {
          count: universe.totalPosts,
          value: numberFormat.format(shownPosts),
        })
      : status === "queued"
        ? t("socialListening.topicPage.status.queued")
        : status === "sorting"
          ? universe.postsPending > 0
            ? t("socialListening.topicPage.status.sorting", {
                count: universe.postsPending,
                value: numberFormat.format(shownPending),
              })
            : t("socialListening.topicActivity.sorting")
          : status === "updated" && updated
            ? t("socialListening.topicPage.meta.updated", { date: updated })
            : t("socialListening.topicCard.notUpdatedYet");
  const isLive = status === "collecting" || status === "sorting";
  // Plain muted text; only live work (collecting, sorting) shows a dot.
  const className = cn(
    "inline-flex shrink-0 items-center gap-2 rounded-sm text-[13px] tabular-nums text-muted-foreground transition-colors",
    onOpenActivity
      ? "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      : null,
  );
  const content: ReactNode = (
    <>
      {isLive ? <StatusDot isLive /> : null}
      <span>{label}</span>
    </>
  );

  if (onOpenActivity) {
    return (
      <button
        type="button"
        className={className}
        title={t("socialListening.topicPage.status.openActivity")}
        data-state={status}
        data-testid="topic-status-pill"
        onClick={onOpenActivity}
      >
        {content}
        <span className="sr-only">
          {t("socialListening.topicPage.status.openActivity")}
        </span>
      </button>
    );
  }

  return (
    <span
      role="status"
      aria-label={t("socialListening.topicPage.status.ariaLabel")}
      className={className}
      data-state={status}
      data-testid="topic-status-pill"
    >
      {content}
    </span>
  );
}

interface StatusDotProps {
  /** A soft, slow halo while work runs. */
  isLive: boolean;
}

/** The status dot, shown only while collecting: primary, with a breathing halo that respects reduced motion. */
function StatusDot(props: StatusDotProps) {
  const { isLive } = props;

  return (
    <span
      aria-hidden="true"
      className="relative inline-flex h-1.5 w-1.5 shrink-0"
      data-live={isLive ? "true" : "false"}
      data-testid="topic-status-dot"
    >
      {isLive ? (
        <span className="absolute inset-0 rounded-full bg-primary/50 [animation-duration:2.4s] motion-safe:animate-ping" />
      ) : null}
      <span className="relative h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.18)]" />
    </span>
  );
}
